import React, { useState, useEffect } from 'react';
import { Calculator, ShoppingCart, XCircle, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const CartAbandonmentRateCalculator = () => {
  const [totalCarts, setTotalCarts] = useState(1000);
  const [completedPurchases, setCompletedPurchases] = useState(300);
  const [abandonmentRate, setAbandonmentRate] = useState(0);

  useEffect(() => {
    calculateAbandonmentRate();
  }, [totalCarts, completedPurchases]);

  const calculateAbandonmentRate = () => {
    const rate = totalCarts > 0 ? ((totalCarts - completedPurchases) / totalCarts) * 100 : 0;
    setAbandonmentRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/cart-abandonment-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Cart Abandonment Rate Calculator: Reduce Lost Sales | Free Tool</title>
        <meta name="description" content="Calculate your Cart Abandonment Rate instantly. Learn how to reduce abandoned carts, recover lost sales, and optimize your checkout process with expert strategies." />
        <meta name="keywords" content="cart abandonment rate calculator, shopping cart abandonment, e-commerce metrics, checkout optimization, abandoned cart recovery" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Cart Abandonment Calculator: Recover Lost Sales" />
        <meta property="og:description" content="Calculate your Cart Abandonment Rate instantly. Get actionable strategies to reduce cart abandonment and boost sales." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Cart Abandonment Calculator: Recover Lost Sales" />
        <meta property="twitter:description" content="Calculate your Cart Abandonment Rate instantly. Get actionable strategies to reduce cart abandonment and boost sales." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Cart Abandonment Rate Calculator",
            "description": "Free calculator to measure and reduce shopping cart abandonment rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Cart Abandonment Rate",
              "Compare with Industry Benchmarks",
              "Get Recovery Strategies",
              "Optimize Checkout Process",
              "Track Lost Sales"
            ],
            "about": {
              "@type": "Thing",
              "name": "Cart Abandonment Rate",
              "description": "The percentage of online shoppers who add items to their cart but leave without completing the purchase."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "E-commerce Managers, Online Retailers, Digital Marketers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Cart Abandonment Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total number of shopping carts"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of completed purchases"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your abandonment rate and recovery strategies"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Cart Abandonment Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Number of Carts</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <ShoppingCart className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalCarts}
                  onChange={(e) => setTotalCarts(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Completed Purchases</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <ShoppingCart className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={completedPurchases}
                  onChange={(e) => setCompletedPurchases(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="300"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {abandonmentRate}%
            <XCircle className="w-8 h-8 ml-2 text-red-400" />
          </div>
          <p className="text-gray-600">Cart Abandonment Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Cart Abandonment Rate is {abandonmentRate}%. This means that {abandonmentRate}% of shoppers who add items to their cart leave without completing the purchase. A lower abandonment rate is generally better, but the average rate varies by industry.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Fashion: 68-70%</li>
          <li>Electronics: 65-67%</li>
          <li>Travel: 80-82%</li>
          <li>Retail: 72-74%</li>
          <li>B2B: 75-77%</li>
          <li>Finance: 75-78%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Common Abandonment Reasons</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Unexpected shipping costs (55%)</li>
          <li>Required account creation (34%)</li>
          <li>Complex checkout process (26%)</li>
          <li>Hidden total costs (21%)</li>
          <li>Website errors/crashes (17%)</li>
          <li>Payment security concerns (15%)</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Recovery Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Simplify the checkout process</li>
          <li>Offer guest checkout options</li>
          <li>Be transparent about all costs early</li>
          <li>Provide multiple payment options</li>
          <li>Implement cart abandonment emails</li>
          <li>Use exit-intent popups with offers</li>
          <li>Improve website loading speed</li>
          <li>Optimize for mobile devices</li>
          <li>Display security badges</li>
          <li>Offer live chat support</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Recovery Impact</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Abandoned cart emails have a 45% open rate</li>
          <li>10% reduction in abandonment can increase revenue by 30%</li>
          <li>Exit-intent popups can recover 10-15% of lost sales</li>
          <li>Retargeting ads can recover 26% of abandoned carts</li>
        </ul>
      </div>
    </div>
  );
};

export default CartAbandonmentRateCalculator;
